import { useEffect, useRef } from 'react';
import { CIEN_CUADRAS_BLUE } from '../../../firebase/firebaseConfig';

declare var L: any;
declare var turf: any;

interface UseMapDrawingInteractionProps {
  mapInstance: any;
  interactionMode: 'none' | 'draw' | 'select';
  setInteractionMode: (mode: 'none' | 'draw' | 'select') => void;
  onSpatialFilterChange: (filter: any) => void;
}

export function useMapDrawingInteraction({
  mapInstance,
  interactionMode,
  setInteractionMode,
  onSpatialFilterChange,
}: UseMapDrawingInteractionProps) {
  const drawingLayerRef = useRef<any>(null);
  const tempPointsRef = useRef<any[]>([]);

  useEffect(() => {
    const map = mapInstance.current;
    if (!map) return;
    const container = map.getContainer();
    container.style.cursor = interactionMode !== 'none' ? 'crosshair' : '';

    if (interactionMode === 'draw') {
      map.dragging.disable();
    } else {
      map.dragging.enable();
    }

    const onMouseDown = (e: any) => {
      if (interactionMode === 'draw' && e.latlng) {
        tempPointsRef.current = [e.latlng];
        if (drawingLayerRef.current) map.removeLayer(drawingLayerRef.current);
        drawingLayerRef.current = L.polyline(tempPointsRef.current, {
          color: CIEN_CUADRAS_BLUE,
          weight: 3,
          dashArray: '5, 5',
        }).addTo(map);
      }
    };

    const onMouseMove = (e: any) => {
      if (interactionMode === 'draw' && tempPointsRef.current.length > 0 && e.latlng) {
        tempPointsRef.current.push(e.latlng);
        drawingLayerRef.current.setLatLngs(tempPointsRef.current);
      }
    };

    const onMouseUp = () => {
      if (interactionMode === 'draw') {
        if (tempPointsRef.current.length >= 3) {
          const coords = tempPointsRef.current.map((p) => [p.lng, p.lat]);
          coords.push(coords[0]);
          const poly = turf.polygon([coords]);
          const center = turf.center(poly).geometry.coordinates;
          onSpatialFilterChange({
            mode: 'polygon',
            center: { lat: center[1], lon: center[0] },
            geometry: poly.geometry,
          });
        }
        if (drawingLayerRef.current) {
          map.removeLayer(drawingLayerRef.current);
          drawingLayerRef.current = null;
        }
        tempPointsRef.current = [];
        setInteractionMode('none');
      }
    };

    const onMapClick = (e: any) => {
      if (interactionMode === 'select' && e.latlng) {
        const circleFeature = turf.circle([e.latlng.lng, e.latlng.lat], 0.6, { units: 'kilometers' });
        circleFeature.properties = { isCircle: true };
        onSpatialFilterChange({
          mode: 'radius',
          center: { lat: e.latlng.lat, lon: e.latlng.lng },
          radius: 600,
          geometry: circleFeature.geometry,
        });
        setInteractionMode('none');
      }
    };

    map.on('mousedown touchstart', onMouseDown);
    map.on('mousemove touchmove', onMouseMove);
    map.on('mouseup touchend', onMouseUp);
    map.on('click', onMapClick);

    return () => {
      map.off('mousedown touchstart', onMouseDown);
      map.off('mousemove touchmove', onMouseMove);
      map.off('mouseup touchend', onMouseUp);
      map.off('click', onMapClick);
    };
  }, [interactionMode, onSpatialFilterChange, mapInstance, setInteractionMode]);
}

